import { useEffect, useState } from "react";
import Modal from "../../common/Modal";
import {
  getHubRoles,
  inviteHubPartner,
} from "../../../services/hubPartnerService";
import { Mail, Shield, CheckCircle } from "lucide-react";

const InvitePartnerModal = ({ hubId, onClose, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [role, setRoleId] = useState("");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getHubRoles();
        const filtered = res.filter((r) => r.name?.toLowerCase() !== "admin");
        setRoles(filtered.filter((r) => r.name === "Operations"));
        if (filtered.length > 0) setRoleId(filtered[0].name);
      } catch {
        // fail silently
      }
    };
    fetch();
  }, []);

  const handleSubmit = async () => {
    if (!email || !role) return;
    setError("");
    setSuccessMessage("");
    setLoading(true);
    try {
      const res = await inviteHubPartner(hubId, email, role);
      if (res.isSuccess) {
        setSuccessMessage(res.message ?? "Invite sent successfully.");
        onSuccess();
      } else {
        setError(res.message ?? "Failed to send invite.");
      }
    } catch (err) {
      setError(err.response?.data?.message ?? "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Invite Hub Partner">
      <div className="space-y-5">
        {successMessage ? (
          // ── Success state ──
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="p-3 rounded-full bg-green-500/10">
              <CheckCircle size={28} className="text-green-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-text-primary mb-1">
                Invite Sent
              </p>
              <p className="text-xs text-text-muted">{successMessage}</p>
            </div>
            <p className="text-xs text-text-hint">
              Email sent with instructions to join the hub. They can also accept the invite directly in the Ingroove app under <span className="text-text-muted font-medium">Profile → Pending Invites</span>.
            </p>
            <div className="flex gap-3 w-full pt-2">
              <button
                onClick={() => {
                  setSuccessMessage("");
                  setEmail("");
                }}
                className="flex-1 py-2.5 rounded-xl text-sm border border-border text-text-muted hover:border-border-hover transition"
              >
                Invite Another
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-sm ingroove-gradient-bg text-white font-semibold hover:opacity-90 transition shadow-glow"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          // ── Form state ──
          <>
            {/* Email */}
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="partner@example.com"
                  className="w-full bg-bg-base border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
                />
              </div>
            </div>

            {/* Role */}
            <div>
              <label className="block text-xs text-text-muted uppercase tracking-wider mb-2">
                Role
              </label>
              <div className="relative">
                <Shield
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-hint"
                />
                <select
                  value={role}
                  onChange={(e) => setRoleId(e.target.value)}
                  className="w-full bg-bg-base border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary transition appearance-none"
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Role descriptions */}
            <div className="bg-bg-base border border-border rounded-xl p-4 space-y-2">
              <p className="text-xs text-text-muted uppercase tracking-wide mb-3">
                Role Permissions
              </p>
              {[
                {
                  name: "Operations",
                  desc: "Can scan tickets and manage attendees at events",
                },
                {
                  name: "Finance",
                  desc: "Can view reports and financial data",
                  soon: true,
                },
                {
                  name: "Content",
                  desc: "Can manage event listings and media",
                  soon: true,
                },
              ].map((r) => (
                <div
                  key={r.name}
                  className="flex items-start justify-between gap-3"
                >
                  <div>
                    <p className="text-xs font-medium text-text-secondary">
                      {r.name}
                    </p>
                    <p className="text-xs text-text-muted">{r.desc}</p>
                  </div>
                  {r.soon && (
                    <span className="flex-shrink-0 text-xs bg-bg-elevated border border-border text-text-hint px-2 py-0.5 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl text-sm border border-border text-text-muted hover:border-border-hover transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !email || !role}
                className="flex-1 py-2.5 rounded-xl text-sm ingroove-gradient-bg text-white font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-glow"
              >
                {loading ? "Sending..." : "Send Invite"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default InvitePartnerModal;
